import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminUploadFile, adminListMedia } from "@/lib/api";
import { UploadCloud, Loader2, X, Link as LinkIcon, Images } from "lucide-react";
import { toast } from "sonner";
import { normalizeSrc } from "@/lib/images";

// Store & render media as same-origin `/api/media/...` paths so they resolve on
// any domain (preview + production), independent of where they were uploaded.
const abs = (u) => normalizeSrc(u);

function LibraryModal({ onPick, onClose }) {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["admin-media"], queryFn: adminListMedia, staleTime: 30_000 });
  const items = (data?.items || []).filter((i) => i.kind === "image" && (!q || (i.original_filename || "").toLowerCase().includes(q.toLowerCase()) || (i.tags || []).join(" ").toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="media-library-modal">
      <div className="max-h-[80vh] w-full max-w-3xl overflow-hidden border border-line bg-surface" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-line p-4">
          <Images className="h-5 w-5 text-saffron" />
          <h3 className="font-serif-latin text-lg text-ink">Image Library</h3>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename or tag…" data-testid="media-library-search" className="ml-auto w-48 border border-line bg-parchment px-3 py-1.5 text-sm text-ink focus:border-saffron focus:outline-none" />
          <button onClick={onClose} className="text-ink-3 hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="max-h-[62vh] overflow-y-auto p-4">
          {isLoading ? (
            <p className="py-16 text-center text-ink-3">Loading…</p>
          ) : items.length === 0 ? (
            <p className="py-16 text-center text-ink-3">No images yet — upload one and it will appear here.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
              {items.map((i) => (
                <button key={i.id} type="button" data-testid="media-library-item" onClick={() => { onPick(abs(i.url)); onClose(); }} className="group relative aspect-square overflow-hidden border border-line hover:border-saffron">
                  <img src={abs(i.url)} alt={i.original_filename || ""} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ImageUploader({ value, onChange, label = "Image", testid = "image", hint }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lib, setLib] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file (JPG, PNG, WEBP or GIF)."); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("Image too large (max 12 MB)."); return; }
    setBusy(true);
    try {
      const res = await adminUploadFile(file);
      onChange(abs(res.url));
      toast("Image uploaded");
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{label}</label>
      {hint && <p className="mb-2 text-xs text-ink-3">{hint}</p>}
      <div className="flex items-start gap-4">
        <div className="relative h-24 w-32 shrink-0 overflow-hidden border border-line bg-parchment">
          {value ? (
            <img src={abs(value)} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-ink-3"><UploadCloud className="h-6 w-6" /></div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-upload-btn`} className="inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              {busy ? "Uploading…" : "Upload"}
            </button>
            <button type="button" onClick={() => setLib(true)} data-testid={`${testid}-library-btn`} className="inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
              <Images className="h-4 w-4" /> Choose from library
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-[var(--saffron-dark)]">
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LinkIcon className="h-3.5 w-3.5 shrink-0 text-ink-3" />
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste an image URL" data-testid={`${testid}-url-input`} className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
          </div>
          <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-file-input`} />
        </div>
      </div>
      {lib && <LibraryModal onPick={onChange} onClose={() => setLib(false)} />}
    </div>
  );
}
